import { Clock, Plus } from "lucide-react"
import { useEffect, useState } from "react"

type TimeEntry = {
  id: string
  userName: string
  date: string
  durationHours: number
  note: string
}

type Props = {
  taskId: string
}

const TaskTimeLog = ({ taskId }: Props) => {
  const [entries, setEntries] = useState<TimeEntry[]>([])
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ duration: "", date: "", note: "" })

  useEffect(() => {
    fetch(`/api/tasks/${taskId}/time-entries`)
      .then((r) => r.json())
      .then((d) => setEntries(d.timeEntries || []))
      .catch(() => {})
  }, [taskId])

  const totalHours = entries.reduce((sum, e) => sum + e.durationHours, 0)

  const handleSubmit = async () => {
    const duration = parseFloat(form.duration)
    if (!duration || duration <= 0) return

    try {
      const res = await fetch(`/api/tasks/${taskId}/time-entries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          duration,
          date: form.date || undefined,
          note: form.note,
        }),
      })
      if (res.ok) {
        // Refresh
        const resp = await fetch(`/api/tasks/${taskId}/time-entries`)
        const data = await resp.json()
        setEntries(data.timeEntries || [])
        setForm({ duration: "", date: "", note: "" })
        setShowForm(false)
      }
    } catch { /* ok */ }
  }

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <label className="flex items-center gap-2 text-xs font-medium text-slate-700">
          <Clock className="h-4 w-4" />
          Time Logged {totalHours > 0 && `(${totalHours.toFixed(1)}h total)`}
        </label>
        <button
          type="button"
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-1 text-xs font-medium text-moss hover:text-moss/80"
        >
          <Plus className="h-3.5 w-3.5" />
          Log Time
        </button>
      </div>

      {showForm && (
        <div className="mb-3 rounded-lg border border-line bg-slate-50 p-3 space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="mb-0.5 block text-xs text-stone">Hours</label>
              <input
                type="number"
                step="0.25"
                min="0"
                value={form.duration}
                onChange={(e) => setForm({ ...form, duration: e.target.value })}
                className="w-full rounded-lg border border-line bg-white px-2 py-1.5 text-sm outline-none focus:border-moss"
                placeholder="1.5"
              />
            </div>
            <div>
              <label className="mb-0.5 block text-xs text-stone">Date</label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="w-full rounded-lg border border-line bg-white px-2 py-1.5 text-sm outline-none focus:border-moss"
              />
            </div>
            <div>
              <label className="mb-0.5 block text-xs text-stone">Note</label>
              <input
                type="text"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                className="w-full rounded-lg border border-line bg-white px-2 py-1.5 text-sm outline-none focus:border-moss"
                placeholder="What did you do?"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSubmit}
              className="rounded-lg bg-moss px-3 py-1.5 text-xs font-medium text-white hover:bg-moss/90"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="rounded-lg border border-line bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {entries.length > 0 ? (
        <div className="space-y-1.5 max-h-48 overflow-y-auto">
          {entries.map((e) => (
            <div key={e.id} className="flex items-center justify-between rounded-lg border border-line bg-white px-3 py-2">
              <div>
                <span className="text-sm font-medium text-slate-900">{e.durationHours.toFixed(1)}h</span>
                <span className="ml-2 text-xs text-stone">{e.userName}</span>
                {e.note && <span className="ml-2 text-xs text-stone">— {e.note}</span>}
              </div>
              <span className="text-xs text-stone">{e.date}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-line bg-slate-50 p-3 text-center text-xs text-stone">
          No time logged yet
        </div>
      )}
    </div>
  )
}

export default TaskTimeLog
